import { IoMdArrowBack } from 'react-icons/io'
import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaUserCheck, FaUserGroup } from 'react-icons/fa6';
import { IoSearchOutline } from 'react-icons/io5';
import { FollowersTab } from '../../components/account/tabs/FollowersTab';
import { FollowingTab } from '../../components/account/tabs/FollowingTab';
import {
    otherUserFollowersList,
    otherUserFollowingList,
    searchOtherUserFollowersList,
    searchotherUserFollowingList,
} from '../../redux/ApiSlice/AccountProfileSlice';

const OtherAccountConnections = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id, type } = useParams();
    const [activeTab, setActiveTab] = useState(type || 'followers');
    const [searchTerm, setSearchTerm] = useState("");
    const [followersPage, setFollowersPage] = useState(1);
    const [followingPage, setFollowingPage] = useState(1);
    const [debouncePending, setDebouncePending] = useState(false);

    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const {
        otherUser_profile,
        User_followingList,
        User_followersList,
        otherFollowersHasMore,
        otherFollowingHasMore,
        loadingMoreConnections,
        otherSearchedFollowersList,
        otherSearchedFollowingList,
        searchingOtherFollowers,
        searchingOtherFollowing,
    } = ProfileDetails || {};

    const trimmedSearchTerm = searchTerm.trim();
    const isSearching = !!trimmedSearchTerm;

    useEffect(() => {
        if (type) setActiveTab(type);
    }, [type]);

    useEffect(() => {
        if (!id) return;
        if (activeTab === 'followers') {
            setFollowersPage(1);
            dispatch(otherUserFollowersList({ id, page: 1 }));
        } else if (activeTab === 'following' || activeTab === 'followings') {
            setFollowingPage(1);
            dispatch(otherUserFollowingList({ id, page: 1 }));
        }
    }, [activeTab, dispatch, id]);

    // Debounced search against the other-user search API
    useEffect(() => {
        if (!id || !trimmedSearchTerm) {
            setDebouncePending(false);
            return;
        }
        setDebouncePending(true);
        const handle = setTimeout(() => {
            setDebouncePending(false);
            if (activeTab === 'followers') {
                dispatch(searchOtherUserFollowersList({ userId: id, q: trimmedSearchTerm }));
            } else if (activeTab === 'following' || activeTab === 'followings') {
                dispatch(searchotherUserFollowingList({ userId: id, q: trimmedSearchTerm }));
            }
        }, 400);
        return () => clearTimeout(handle);
    }, [trimmedSearchTerm, activeTab, id, dispatch]);

    const handleLoadMoreFollowers = useCallback(() => {
        const nextPage = followersPage + 1;
        setFollowersPage(nextPage);
        dispatch(otherUserFollowersList({ id, page: nextPage, isLoadMore: true }));
    }, [followersPage, id, dispatch]);

    const handleLoadMoreFollowing = useCallback(() => {
        const nextPage = followingPage + 1;
        setFollowingPage(nextPage);
        dispatch(otherUserFollowingList({ id, page: nextPage, isLoadMore: true }));
    }, [followingPage, id, dispatch]);

    const handleTabChange = (tabValue) => {
        setActiveTab(tabValue);
        setSearchTerm("");
    };

    return (
        <>
            <div className="sticky top-0 z-50 bg-white dark:bg-[#1E1D21]">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate(-1)}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black dark:text-black">
                        {otherUser_profile?.full_name || "User"}'s Connections
                    </p>
                </div>

                {/* Tabs Navigation */}
                <div className="flex items-center justify-center gap-x-2 mx-2 bg-white dark:bg-[#1E1D21]">
                    <div
                        onClick={() => handleTabChange('followers')}
                        className={`flex justify-center items-center w-full py-2 cursor-pointer border-b-2 ${activeTab === 'followers'
                            ? 'border-light-blue-400 dark:border-blue-500'
                            : 'border-transparent'
                            }`}
                    >
                        <FaUserGroup className={`text-[15px] ${activeTab === 'followers'
                            ? 'text-light-blue-400 dark:text-blue-500'
                            : 'text-black dark:text-white'
                            }`} />
                        <p className={`text-[12px] ml-2 font-semibold ${activeTab === 'followers'
                            ? 'text-light-blue-400 dark:text-blue-500'
                            : 'text-black dark:text-white'
                            }`}>
                            Followers
                        </p>
                    </div>
                    <div
                        onClick={() => handleTabChange('following')}
                        className={`flex justify-center items-center w-full py-2 cursor-pointer border-b-2 ${activeTab === 'following'
                            ? 'border-light-blue-400 dark:border-blue-500'
                            : 'border-transparent'
                            }`}
                    >
                        <FaUserCheck className={`text-[15px] ${activeTab === 'following'
                            ? 'text-light-blue-400 dark:text-blue-500'
                            : 'text-black dark:text-white'
                            }`} />
                        <p className={`text-[12px] ml-2 font-semibold ${activeTab === 'following'
                            ? 'text-light-blue-400 dark:text-blue-500'
                            : 'text-black dark:text-white'
                            }`}>
                            Following
                        </p>
                    </div>
                </div>

                {/* Search Input - Sticky below tabs */}
                <div className="relative m-2">
                    <IoSearchOutline className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[18px] text-black" />
                    <input
                        type="text"
                        value={searchTerm}
                        placeholder={activeTab === 'followers' ? "Find Followers..." : "Find Following..."}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full text-[14px] pl-8 pr-2 py-1.5 rounded-lg bg-gray-100 dark:bg-[#333333] dark:text-white dark:placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#BDE6FF]"
                    />
                </div>
            </div>

            {/* Tab Content */}
            <div className="bg-white dark:bg-[#1E1D21] h-full w-full" >
                {activeTab === 'followers' && (
                    <FollowersTab
                        datalist={isSearching ? otherSearchedFollowersList : User_followersList}
                        otheruser_id={id}
                        searchTerm={searchTerm}
                        hasMore={isSearching ? false : otherFollowersHasMore}
                        loadingMore={loadingMoreConnections}
                        onLoadMore={handleLoadMoreFollowers}
                        serverFiltered={isSearching}
                        searching={isSearching && (debouncePending || searchingOtherFollowers)}
                    />
                )}
                {activeTab === 'following' && (
                    <FollowingTab
                        datalist={isSearching ? otherSearchedFollowingList : User_followingList}
                        otheruser_id={id}
                        searchTerm={searchTerm}
                        hasMore={isSearching ? false : otherFollowingHasMore}
                        loadingMore={loadingMoreConnections}
                        onLoadMore={handleLoadMoreFollowing}
                        serverFiltered={isSearching}
                        searching={isSearching && (debouncePending || searchingOtherFollowing)}
                    />
                )}
            </div>
        </>
    )
}

export default OtherAccountConnections